import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "../../../styles/ag-grid-custom.css";
import {
  CellStyleModule,
  ColDef,
  PaginationModule,
  themeQuartz,
  ModuleRegistry,
} from "ag-grid-community";
import images from "../../../assets/images";
import { Plus, Edit, Trash2, Eye } from "lucide-react";
import { SpaceUtilization } from "../../../types/api";
import {
  useSpaceUtilizationStore,
  usePremisesStore,
} from "../../../store/store";
import { toast } from "react-toastify";
import AddSpaceUtilization from "./AddSpaceUtilization";
import { useLocationStore } from "../../../store/masterDataStore";
import { SpinLoader } from "../../../components/loader/Loader";

ModuleRegistry.registerModules([CellStyleModule, PaginationModule]);

// const gridTheme = themeQuartz.withParams({
//   accentColor: "#93AA9A",
//   headerHeight: "200px",
//   browserColorScheme: "light",
//   headerFontSize: 14,
// });

const SpaceManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [rowData, setRowData] = useState<SpaceUtilization[]>([]);
  const [isModal, setIsModal] = useState<boolean>(false);
  const [mode, setMode] = useState<"create" | "edit" | "view">("create");
  const [selectedData, setSelectedData] = useState<SpaceUtilization | null>(
    null
  );
  const [filterPremiseId, setFilterPremiseId] = useState<string>("");
  const [filterUtilizationType, setFilterUtilizationType] = useState<
    "before" | "after" | ""
  >("");

  const {
    spaceUtilizations,
    fetchSpaceUtilizations,
    deleteSpaceUtilization,
    loading: storeLoading,
  } = useSpaceUtilizationStore();
  const { allPremises, fetchPremises } = usePremisesStore();
  const { allLocations, fetchAllLocations } = useLocationStore();

  useEffect(() => {
    fetchPremises();
    fetchAllLocations();
    fetchSpaceUtilizations();
  }, [fetchSpaceUtilizations, fetchPremises, fetchAllLocations]);

  useEffect(() => {
    if (spaceUtilizations && Array.isArray(spaceUtilizations)) {
      setRowData(spaceUtilizations);
    } else {
      setRowData([]);
    }
  }, [spaceUtilizations]);

  useEffect(() => {
    setLoading(storeLoading);
  }, [storeLoading]);

  const getPremiseName = (premiseId: string) => {
    if (!premiseId) return "";
    const premise = allPremises.find(
      (p: { _id?: string; id?: string; premisesName: string }) =>
        p._id === premiseId || p.id === premiseId
    );
    return premise ? premise.premisesName : "N/A";
  };

  const getLocationName = (locationId: string) => {
    if (!locationId) return "";
    const location = allLocations.find((l) => l._id === locationId);
    return location ? location.room : "N/A";
  };

  const handleView = (data: SpaceUtilization) => {
    setSelectedData(data);
    setMode("view");
    setIsModal(true);
  };

  const handleEdit = (data: SpaceUtilization) => {
    setSelectedData(data);
    setMode("edit");
    setIsModal(true);
  };

  const handleDelete = useCallback(
    async (id: string) => {
      if (
        window.confirm(
          "Are you sure you want to delete this space utilization record?"
        )
      ) {
        try {
          await deleteSpaceUtilization(id);
        } catch (error) {
          console.error("Error deleting space utilization:", error);
        }
      }
    },
    [deleteSpaceUtilization]
  );

  const handleAdd = () => {
    setSelectedData(null);
    setMode("create");
    setIsModal(true);
  };

  const handleClose = () => {
    setIsModal(false);
    setSelectedData(null);
  };

  const handleSubmit = async (data: SpaceUtilization) => {
    handleClose();
    await fetchSpaceUtilizations({
      premiseId: filterPremiseId || undefined,
      utilizationType: filterUtilizationType || undefined,
    });
  };

  const handleFilter = () => {
    fetchSpaceUtilizations({
      premiseId: filterPremiseId || undefined,
      utilizationType: filterUtilizationType || undefined,
    });
  };

  const columnDefs = useMemo(
    (): ColDef<SpaceUtilization>[] => [
      {
        headerName: "Area Name",
        field: "areaName",
        sortable: true,
        filter: false,
        flex: 1,
      },
      {
        headerName: "Premise",
        field: "premiseId",
        sortable: true,
        filter: false,
        flex: 1,
        valueGetter: (params: any) => getPremiseName(params.data?.premiseId),
      },
      {
        headerName: "Location/Area",
        field: "areaId",
        sortable: true,
        filter: false,
        flex: 1,
        valueGetter: (params: any) => getLocationName(params.data?.areaId),
      },
      {
        headerName: "Type",
        field: "utilizationType",
        sortable: true,
        filter: false,
        flex: 1,
        cellRenderer: (params: any) => {
          const type = params.value;
          return (
            <span
              className={`px-2 py-1 rounded text-xs font-medium ${type === "before"
                  ? "bg-blue-100 text-blue-800"
                  : "bg-green-100 text-green-800"
                }`}
            >
              {type === "before" ? "Before" : "After"}
            </span>
          );
        },
      },
      {
        headerName: "Area Size",
        field: "areaSize",
        sortable: true,
        filter: false,
        flex: 1,
        valueGetter: (params: any) => {
          const size = params.data?.areaSize;
          const unit = params.data?.unit || "sqft";
          return size ? `${size} ${unit}` : "N/A";
        },
      },
      {
        headerName: "Utilization %",
        field: "utilizationPercentage",
        sortable: true,
        filter: false,
        flex: 1,
        valueGetter: (params: any) => {
          const percentage = params.data?.utilizationPercentage;
          return percentage !== null && percentage !== undefined
            ? `${percentage}%`
            : "N/A";
        },
      },
      {
        headerName: "Purpose",
        field: "purpose",
        sortable: true,
        filter: false,
        flex: 1,
      },
      {
        headerName: "Department",
        field: "department",
        sortable: true,
        filter: false,
        flex: 1,
      },
      {
        headerName: "Project",
        field: "projectName",
        sortable: true,
        filter: false,
        flex: 1,
      },
      {
        headerName: "Recorded Date",
        field: "recordedDate",
        sortable: true,
        filter: false,
        flex: 1,
        valueGetter: (params: any) => {
          const date = params.data?.recordedDate;
          return date ? new Date(date).toLocaleDateString() : "N/A";
        },
      },
      {
        headerName: "Actions",
        field: "actions" as unknown as keyof SpaceUtilization,
        sortable: false,
        filter: false,
        flex: 1,
        cellRenderer: (params: any) => {
          const data = params.data as SpaceUtilization;
          return (
            <div className="flex gap-2 items-center h-full">
              <button
                onClick={() => handleView(data)}
                className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                title="View"
              >
                <img className="iconTd cursor-pointer" src={images.viewIcon} />
              </button>
              <button
                onClick={() => handleEdit(data)}
                className="p-1 text-green-600 hover:bg-green-50 rounded"
                title="Edit"
              >
                <img className="iconTd cursor-pointer" src={images.editIcon} />
              </button>
              <button
                onClick={() => handleDelete(data._id || data.id || "")}
                className="p-1 text-red-600 hover:bg-red-50 rounded"
                title="Delete"
              >
                <Trash2 size={18} />
              </button>
            </div>
          );
        },
      },
    ],
    [allPremises, allLocations]
  );

  return (
    <div className="px-2 py-4 mt-14 w-full h-full">
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Space Management
            </h1>
            <p className="text-gray-600">
              Manage space utilization records before and after
              construction/renovation
            </p>
          </div>
          <button
            onClick={handleAdd}
            className="bg-teal-600 text-white px-6 py-2 rounded-full font-medium hover:bg-teal-700 transition-colors flex items-center gap-2 opacity-70 cursor-not-allowed"
          >
            <Plus size={20} />
            Add Space Record
          </button>
        </div>

        {/* Filters */}
        <div className="mb-4 flex gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Premise
            </label>
            <select
              value={filterPremiseId}
              onChange={(e) => setFilterPremiseId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#93AA9A]"
            >
              <option value="">All Premises</option>
              {allPremises.map(
                (premise: {
                  _id?: string;
                  id?: string;
                  premisesName: string;
                }) => (
                  <option
                    key={premise._id || premise.id}
                    value={premise._id || premise.id}
                  >
                    {premise.premisesName}
                  </option>
                )
              )}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Utilization Type
            </label>
            <select
              value={filterUtilizationType}
              onChange={(e) =>
                setFilterUtilizationType(
                  e.target.value as "before" | "after" | ""
                )
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#93AA9A]"
            >
              <option value="">All Types</option>
              <option value="before">Before</option>
              <option value="after">After</option>
            </select>
          </div>
          <button
            onClick={handleFilter}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
          >
            Filter
          </button>
        </div>

        {loading && <SpinLoader />}

        <div
          className="ag-theme-alpine"
          style={{ height: "600px", width: "100%" }}
        >
          <AgGridReact
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={{
              resizable: true,
              sortable: true,
            }}
            animateRows={true}
            rowSelection="single"
            pagination={true}
            paginationPageSize={20}
            loading={loading}
            rowHeight={50}
            headerHeight={65}
            getRowStyle={() => {
              return {
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                marginBottom: "8px",
                borderRadius: "4px",
                backgroundColor: "white",
                height: "60px",
                padding: "10px 0 0 5px",
                alignItems: "center",
              };
            }}
          />
        </div>
      </div>

      {isModal && (
        <AddSpaceUtilization
          isOpen={isModal}
          onClose={handleClose}
          onSubmit={handleSubmit}
          mode={mode}
          initialData={selectedData}
        />
      )}
    </div>
  );
};

export default SpaceManagement;
